/**
 * The text-matching primitives every picker in the app shares.
 *
 * Pickers differ in what they are picking — people match on name, email and
 * initials; rounds and proposals match on their name and shortcode — but they
 * must all agree on what "matches" means at the character level, so that
 * "josé" and "Jose" find each other everywhere and a stray double space never
 * hides a row. Those rules live here; each picker's notion of *which fields*
 * to look at lives next to that picker's own type (see ./user-search).
 */

/**
 * Lowercase, strip accents, collapse runs of whitespace. Applied to both sides
 * of every comparison so "josé" and "Jose" find each other in either direction.
 */
export function normalizeSearchText(value?: string | null): string {
  if (!value) return "";
  return (
    value
      .normalize("NFD")
      // Combining marks left behind by NFD — dropping them folds "é" to "e".
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ")
  );
}

/**
 * Does any of these fields contain the query as a substring? The building
 * block for the common case — a thing with a name, and maybe a code or a
 * second label people also type.
 *
 * A blank query matches, so callers can pass raw input straight through
 * without special-casing the empty box.
 */
export function matchesAnyField(
  query: string,
  fields: ReadonlyArray<string | null | undefined>,
): boolean {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  return fields.some((field) =>
    normalizeSearchText(field).includes(normalizedQuery),
  );
}

/**
 * Filter a list, preserving the caller's ordering — pickers order their list
 * themselves (rounds you're in first, recently used first, and so on) and
 * searching must not reshuffle it.
 */
export function filterBySearch<T>(
  items: readonly T[],
  query: string,
  matches: (item: T, query: string) => boolean,
): T[] {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [...items];
  return items.filter((item) => matches(item, query));
}
